import React, { useState } from 'react';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
} from '@mui/material';
import { IUser } from '../../../utils/entities/interface';
import {
  capitalizeWords,
  formatDateAndTime,
  formatPhoneNumber,
  getStatusLabel,
} from '../../../utils/helper';
import { ToggleUserStatus } from '../../../services/users';
import { useNavigate } from 'react-router-dom';
import { ModalConfirmation } from '../../molecules/ModalConfirmation';

const ColGroup = ({
  headerCells,
  showCheckbox,
}: {
  headerCells: [string, number][];
  showCheckbox: boolean;
}) => {
  const totalWidth = headerCells.reduce((sum, [_, w]) => sum + w, 0);
  return (
    <colgroup>
      {showCheckbox && (
        <col style={{ width: '40px', minWidth: '40px', maxWidth: '40px' }} />
      )}
      {headerCells.map(([_, width], i) => (
        <col key={i} style={{ width: `${(width / totalWidth) * 100}%` }} />
      ))}
    </colgroup>
  );
};

export const UserList = ({
  users,
  onUpdate,
  isLoading,
  headerCells,
  showCheckbox = false,
  selectedIds = [],
  onSelectionChange,
}: {
  users: IUser[];
  onUpdate: () => void;
  headerCells: [string, number][];
  isLoading: boolean;
  showCheckbox?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
}) => {
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);

  const handleOpenModal = (user: IUser) => {
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setOpenModal(false);
  };

  const handleConfirmToggle = async () => {
    if (!selectedUser) return;
    await ToggleUserStatus(selectedUser._id);
    onUpdate();
    handleCloseModal();
  };

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange(users.map((u) => u._id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    }
  };

  const isAllSelected = users.length > 0 && selectedIds.length === users.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < users.length;

  const renderCell = (user: IUser, colIndex: number) => {
    switch (colIndex) {
      case 0:
        return capitalizeWords(user.name) || 'N/A';
      case 1:
        return formatPhoneNumber(user.phoneNumber);
      case 2:
        return ['RETAIL', 'HNI', 'SME', 'LARGE_SCALE', 'OEM'].includes(
          user.type,
        )
          ? getStatusLabel(user.type)
          : 'N/A';
      case 3:
        return user.email || 'N/A';
      case 4:
        return formatDateAndTime(user.createdAt);
      case 5:
        return (
          <Button
            variant="outlined"
            size="small"
            color={user.isActive ? 'error' : 'success'}
            onClick={() => handleOpenModal(user)}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              borderRadius: '8px',
            }}
          >
            {user.isActive ? 'Disable' : 'Enable'}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TableContainer
        sx={{
          overflow: 'hidden',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Table
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: '0 5px',
            }}
          >
            <ColGroup headerCells={headerCells} showCheckbox={showCheckbox} />
            <TableHead>
              <TableRow>
                {showCheckbox && (
                  <TableCell
                    sx={{
                      textAlign: 'center',
                      color: '#FFFFFF',
                      backgroundColor: '#1C1C1C',
                      padding: '16px 4px',
                      borderTop: '1px solid #8A8D91',
                      borderBottom: '1px solid #8A8D91',
                      borderLeft: '1px solid #8A8D91',
                      borderTopLeftRadius: 10,
                      borderBottomLeftRadius: 10,
                      width: '40px',
                      minWidth: '40px',
                      maxWidth: '40px',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      sx={{
                        color: '#8A8D91',
                        '&.Mui-checked': { color: '#E31E25' },
                        '&.MuiCheckbox-indeterminate': { color: '#E31E25' },
                        p: 0,
                      }}
                    />
                  </TableCell>
                )}
                {headerCells.map(([label, width], index) => (
                  <TableCell
                    key={index}
                    sx={{
                      textAlign: 'center',
                      color: '#FFFFFF',
                      backgroundColor: '#1C1C1C',
                      width,
                      minWidth: width,
                      maxWidth: width,
                      boxSizing: 'border-box',
                      padding: '25px 0px',
                      borderTop: '1px solid #8A8D91',
                      borderBottom: '1px solid #8A8D91',
                      borderLeft:
                        !showCheckbox && index === 0
                          ? '1px solid #8A8D91'
                          : 'none',
                      borderRight:
                        index === headerCells.length - 1
                          ? '1px solid #8A8D91'
                          : 'none',
                      borderTopLeftRadius: !showCheckbox && index === 0 ? 10 : 0,
                      borderBottomLeftRadius:
                        !showCheckbox && index === 0 ? 10 : 0,
                      borderTopRightRadius:
                        index === headerCells.length - 1 ? 10 : 0,
                      borderBottomRightRadius:
                        index === headerCells.length - 1 ? 10 : 0,
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>
        </Box>

        <Box
          data-scroll-body
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            scrollbarWidth: 'none',
          }}
        >
          <Table
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: '0 8px',
            }}
          >
            <ColGroup headerCells={headerCells} showCheckbox={showCheckbox} />
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={headerCells.length + (showCheckbox ? 1 : 0)}
                    align="center"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={headerCells.length + (showCheckbox ? 1 : 0)}
                    align="center"
                  >
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      backgroundColor: '#1C1C1C',
                      '& > td': {
                        borderTop: '1px solid #3E3E3E',
                        borderBottom: '1px solid #3E3E3E',
                        color: '#8A8D91',
                        paddingY: '10px',
                        textAlign: 'center',
                        backgroundColor: '#1C1C1C',
                      },
                      '& > td:first-of-type': {
                        borderLeft: '1px solid #3E3E3E',
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: 12,
                      },
                      '& > td:last-of-type': {
                        borderRight: '1px solid #3E3E3E',
                        borderTopRightRadius: 12,
                        borderBottomRightRadius: 12,
                      },
                    }}
                  >
                    {showCheckbox && (
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          padding: '8px 4px',
                          width: '40px',
                          minWidth: '40px',
                          maxWidth: '40px',
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(user._id)}
                          onChange={(e) =>
                            handleSelectOne(user._id, e.target.checked)
                          }
                          sx={{
                            color: '#8A8D91',
                            '&.Mui-checked': { color: '#E31E25' },
                            p: 0,
                          }}
                        />
                      </TableCell>
                    )}
                    {headerCells.map(([label, width], colIdx) => (
                      <TableCell
                        key={colIdx}
                        sx={{
                          width,
                          minWidth: width,
                          maxWidth: width,
                          boxSizing: 'border-box',
                          textAlign: 'center',
                        }}
                      >
                        {label === 'Action' ? (
                          <IconButton
                            sx={{ color: '#E31E25' }}
                            onClick={() => navigate(`/customers/${user._id}`)}
                          >
                            <RemoveRedEyeOutlinedIcon />
                          </IconButton>
                        ) : (
                          renderCell(user, colIdx)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>

      {/* Confirmation Modal */}
      <ModalConfirmation
        open={openModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirmToggle}
        selectedItem={selectedUser}
        entityName="user"
      />
    </>
  );
};
